import React, { useEffect, useState } from 'react'
import { withStyles } from '@material-ui/core'
import styles from './styles'
import URL from 'src/api/url'
import CoreNavigation from 'src/components/core/core-tab'
import {
  HeaderSettingsLanguageAccordion,
  HeaderSettingsCollectionLanguageAccordion,
} from '../index'
import HeaderSettingsImage from '../header-settings-image'
import HeaderSettingsDescription from '../header-settings-description'
import HeaderSettingsColors from '../header-settings-colors'
import HeaderSettingsAlignment from '../header-settings-default-alignment'
import HeaderSettingsHeroAlignment from '../header-settings-hero-allignment'
import HeaderSettingsPricing from '../header-settings-pricing'
import HeaderSettingsCard from '../header-settings-card'
import { useSelector } from 'react-redux'
import CollectionTitleEdit from '../collection-title-edit'
import { editorEnum } from 'src/model/editor/editorEnum'
const HeaderSettingsTheme = ({
  classes,
  landingPageData,
  themeSettings,
  onThemeSettingsChange,
}) => {
  // Track if initialization has happened
  const [initialized, setInitialized] = useState(false)
  const [selectedTab, setSelectedTab] = useState('header-settings')
  const [priceTagActive, setPriceTagActive] = useState(false)
  const [selectedCurrency, setSelectedCurrency] = useState({
    symbol: '$',
    code: 'USD',
    name: '$ (USD)',
  })
  const collection = useSelector(state => state.collection)
  const [priceValue, setPriceValue] = useState('')
  const pageUrl = window.location.pathname.split('/')[1]

  // Separate alignment states for each header type
  const [typeAlignments, setTypeAlignments] = useState({
    1: 'center', // Default type alignment
    2: 'left', // Hero type alignment
    3: 'center', // Promo type alignment
  })

  // Language state management with defaults based on page type
  const [selectedStartValue, setSelectedStartValue] = useState(
    pageUrl === 'mini-course' ? 'Start Course' : 'Get Started',
  )
  const [selectedResumeValue, setSelectedResumeValue] = useState(
    'Resume Course',
  )
  const [selectedReadMoreValue, setSelectedReadMoreValue] = useState('See more')
  const landingPageSection = landingPageData?.sections?.find(
    x => x.type === editorEnum.headerType,
  )
  // Local state for type preview (not saved until Save button is clicked)
  const [previewType, setPreviewType] = useState(
    landingPageSection?.content?.type || 1,
  )

  // State for description value
  const [descriptionValue, setDescriptionValue] = useState(
    landingPageSection?.content?.description || '',
  )

  // State for color properties (for type 2)
  const [colorProperties, setColorProperties] = useState({
    backgroundColor: landingPageSection?.content?.backgroundColor || '#FFFFFF',
    textColor: landingPageSection?.content?.textColor || '#002E47',
  })
  // Initialize theme settings from landing page data only once
  useEffect(() => {
    // Only initialize if we haven't done so yet and we have landingPageData
    if (landingPageData && !initialized) {
      const content = landingPageSection?.content || {}

      // Set our initialization flag to prevent overriding user selections
      setInitialized(true)

      // Initialize type-specific alignments
      const currentType = content.type || 1
      const currentAlignment = content.alignment || 'center'

      setTypeAlignments(prev => ({
        ...prev,
        [currentType]: currentAlignment,
      }))

      // Set the current alignment based on the current type
      onThemeSettingsChange('selectedPosition', currentAlignment)

      if (!themeSettings.background) {
        onThemeSettingsChange('background', content.image || '')
      }

      // Initialize description value
      setDescriptionValue(content.description || '')

      // Initialize price tag data
      if (content.priceTagActive !== undefined) {
        setPriceTagActive(content.priceTagActive)
        onThemeSettingsChange('priceTagActive', content.priceTagActive)
      }

      if (content.priceValue !== undefined) {
        setPriceValue(content.priceValue)
        onThemeSettingsChange('priceValue', content.priceValue)
      }

      if (content.selectedCurrency) {
        setSelectedCurrency(content.selectedCurrency)
        onThemeSettingsChange('selectedCurrency', content.selectedCurrency)
      }

      // Initialize language values from localizations or use defaults
      const defaultStart =
        pageUrl === 'mini-course' ? 'Start Course' : 'Get Started'
      const defaultResume = 'Resume Course'
      const defaultReadMore = 'See more'

      setSelectedStartValue(
        landingPageData?.localizations?.startButton || defaultStart,
      )
      setSelectedResumeValue(
        landingPageData?.localizations?.resumeButton || defaultResume,
      )
      setSelectedReadMoreValue(
        landingPageData?.localizations?.readMore || defaultReadMore,
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [landingPageData, initialized, pageUrl])

  // Update preview type when themeSettings.selectedType changes
  useEffect(() => {
    if (themeSettings.selectedType !== undefined) {
      setPreviewType(themeSettings.selectedType)

      // Update alignment based on the selected type
      const newAlignment = typeAlignments[themeSettings.selectedType]
      if (newAlignment) {
        onThemeSettingsChange('selectedPosition', newAlignment)
      }

      // If type changes from 3 to something else and user is on Card Settings tab,
      // switch them back to Header Settings
      if (themeSettings.selectedType !== 3 && selectedTab === 'card-settings') {
        setSelectedTab('header-settings')
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [themeSettings.selectedType, selectedTab])

  // Handle tab selection
  const handleTabSelect = tabValue => {
    setSelectedTab(tabValue)
  }

  // Handle alignment changes for type-specific alignment states
  const handleAlignmentChange = position => {
    const currentType = previewType
    setTypeAlignments(prev => ({
      ...prev,
      [currentType]: position,
    }))
    onThemeSettingsChange('selectedPosition', position)
  }

  // Handle button text changes
  const handleButtonTextChange = (type, value) => {
    if (type === 'start') {
      setSelectedStartValue(value)
      onThemeSettingsChange('startButton', value)
    } else if (type === 'resume') {
      setSelectedResumeValue(value)
      onThemeSettingsChange('resumeButton', value)
    } else if (type === 'readMore') {
      setSelectedReadMoreValue(value)
      onThemeSettingsChange('readMore', value)
    }
  }
  const handlePriceTagToggle = checked => {
    setPriceTagActive(checked)
    onThemeSettingsChange('priceTagActive', checked)
  }
  // Handle description change
  const handleDescriptionChange = (property, value) => {
    if (property === 'description') {
      setDescriptionValue(value)
    }
    onThemeSettingsChange(property, value)
  }

  // Handle color properties change
  const handleColorPropertiesChange = (property, value) => {
    if (property === 'colorProperties') {
      setColorProperties(value)
    }
    onThemeSettingsChange(property, value)
  }

  // Handle price tag changes
  const handlePriceTagChange = (property, value) => {
    if (property === 'priceTagActive') {
      setPriceTagActive(value)
    } else if (property === 'priceValue') {
      setPriceValue(value)
    } else if (property === 'selectedCurrency') {
      setSelectedCurrency(value)
    }
    onThemeSettingsChange(property, value)
  }

  const background =
    themeSettings.background === null ||
    themeSettings.background === '' ||
    themeSettings.background === undefined
      ? 'https://cdn.minicoursegenerator.com/assets/landing-page-templates/thumbnails/Default.png'
      : themeSettings.background.includes('http')
      ? themeSettings.background
      : URL.CDN + themeSettings.background

  const settingSelectorData = [
    {
      label: 'Header Settings',
      value: 'header-settings',
      isActive: selectedTab === 'header-settings',
      onClick: () => handleTabSelect('header-settings'),
    },
    // Only show Card Settings tab when type is 3 (Promo)
    ...(previewType === 3
      ? [
          {
            label: 'Card Settings',
            value: 'card-settings',
            isActive: selectedTab === 'card-settings',
            onClick: () => handleTabSelect('card-settings'),
            disabled: pageUrl !== 'mini-course',
          },
        ]
      : []),
  ]
  const collectionGeneralSettings = useSelector(state => state.collection)

  // Check if this is a showcase collection
  const isShowcaseCollection = collection?.collection?.type === 1

  return (
    <div className={classes.container}>
      {/* Tab Navigation */}
      <div className={classes.tabContainer}>
        <CoreNavigation
          tabList={settingSelectorData}
          color="secondary"
          backgroundColor="primary"
          size="medium"
          alignment="horizontal"
          iconPosition="start"
          fullWidth={false}
        />
      </div>

      {/* Conditional content based on selected tab */}
      {selectedTab === 'header-settings' && (
        <>
          {/* Image section */}
          <HeaderSettingsImage
            background={background}
            primaryColor={landingPageData?.primaryColor}
            previewType={previewType}
            onThemeSettingsChange={onThemeSettingsChange}
          />
          {pageUrl === 'collection' && <CollectionTitleEdit />}
          {/* Description section - Only show for type 2 (Hero) or 3 (Promo) */}
          {(previewType === 2 || previewType === 3) && (
            <HeaderSettingsDescription
              descriptionValue={descriptionValue}
              onThemeSettingsChange={handleDescriptionChange}
            />
          )}

          {/* Alignment section - Only show for type 1 (Default) */}
          {previewType === 1 && (
            <HeaderSettingsAlignment
              themeSettings={themeSettings}
              onThemeSettingsChange={handleAlignmentChange}
              pageUrl={pageUrl}
            />
          )}

          {/* Hero Alignment section - Only show for type 2 (Hero) */}
          {previewType === 2 && (
            <HeaderSettingsHeroAlignment
              themeSettings={themeSettings}
              onThemeSettingsChange={handleAlignmentChange}
              pageUrl={pageUrl}
            />
          )}
          {/* Color Properties section - Only show for type 2 (Hero) */}
          {previewType === 2 && (
            <HeaderSettingsColors
              colorProperties={colorProperties}
              onThemeSettingsChange={handleColorPropertiesChange}
            />
          )}
          {/* Pricing Settings - Only show for collection pages */}
          {pageUrl === 'collection' &&
            collectionGeneralSettings.collection.type !== 1 && (
              <HeaderSettingsPricing
                header={false}
                showPriceTag={true}
                priceTagActive={priceTagActive}
                onThemeSettingsChange={handlePriceTagChange}
              />
            )}
        </>
      )}
      {/* Language Accordion - Different for course and collection */}
      {pageUrl === 'mini-course' && selectedTab === 'header-settings' ? (
        // Mini-course: Start Course, Resume Course, and See More (only type 2)
        <HeaderSettingsLanguageAccordion
          landingPageData={landingPageData}
          onButtonTextChange={handleButtonTextChange}
          isLoading={false}
          selectedStartValue={selectedStartValue}
          selectedResumeValue={selectedResumeValue}
          selectedReadMoreValue={selectedReadMoreValue}
          previewType={previewType}
          pageUrl={pageUrl}
        />
      ) : (
        // Collection: Get Started (type 2 & 3) + See More (only type 2)
        // For showcase collections: only show for type 2 (Hero) with "See more" button only
        // For regular collections: show for both type 2 and type 3
        (previewType === 2 || (previewType === 3 && !isShowcaseCollection)) &&
        selectedTab === 'header-settings' && (
          <HeaderSettingsCollectionLanguageAccordion
            landingPageData={landingPageData}
            onButtonTextChange={handleButtonTextChange}
            isLoading={false}
            selectedReadMoreValue={selectedReadMoreValue}
            selectedStartValue={selectedStartValue}
            previewType={previewType}
            isShowcaseCollection={isShowcaseCollection}
          />
        )
      )}

      {selectedTab === 'card-settings' && (
        <HeaderSettingsCard
          themeSettings={themeSettings}
          onThemeSettingsChange={onThemeSettingsChange}
        />
      )}
    </div>
  )
}

export default withStyles(styles)(HeaderSettingsTheme)
